/**
 * Az élő közvetítés publikus nyomvonala — KÖZÖS a klienssel és a szerverrel.
 *
 * Weben a `useLivePublish` hívja, natív appban a szerver
 * (`server/src/lib/liveReplay.ts`), amikor a natív réteg által küldött
 * mintákból építi a `liveStreams/{uid}` dokumentumot. Ezért nincs benne
 * React, Firebase és DOM — a szerver Docker-buildje be is másolja.
 */
import { encodePolyline, simplifyTrace } from '@/game/polyline';
import type { TracePoint } from '@/types';

/** Ennyi pontnál többet egy overlay-méretű térkép sem mutat meg. */
export const MAX_PUBLISH_POINTS = 400;

/**
 * A publikus nyomvonal: a teljes útvonal, csak megjelenítésre egyszerűsítve.
 *
 * Privátzóna-vágás itt NINCS — lásd a `livePublish.ts` fejlécének 2. pontját.
 * Az egyszerűsítés célja kizárólag a dokumentum mérete: egy overlay-méretű
 * térképen néhány száz pontnál több úgysem látszik.
 */
export function publicRoute(points: readonly TracePoint[]): string {
  if (points.length < 2) return '';
  return encodePolyline(reduceRoute(points, MAX_PUBLISH_POINTS));
}

function reduceRoute(points: readonly TracePoint[], maxPoints: number): TracePoint[] {
  let reduced = simplifyTrace(points, 8);
  for (let epsilon = 16; reduced.length > maxPoints && epsilon <= 512; epsilon *= 2) {
    reduced = simplifyTrace(points, epsilon);
  }
  return reduced;
}

/*
 * ── SECOND-BY-SECOND PLAYBACK (2026-10-24, Geri's decision: option "b") ──
 *
 * The native broadcaster sends every 10 s (battery), yet the viewer moves
 * every second: the document also carries the latest points WITH
 * timestamps (`tail`), and the viewer walks through them with a playhead
 * (`src/screens/liveSnapshot.ts` → `playbackFrame`).
 *
 * Playback can only show the already published past, so the server cuts
 * `LIVE_PLAYBACK_LAG_MS` later than the requested delay and the viewer plays
 * that much behind — the VISIBLE delay stays the requested one (Twitch sync).
 * Cost: the raw document is that much fresher than the requested delay
 * (~3 s at 15 s) — accepted by Geri.
 */

/**
 * Playback buffer: upper estimate of the gap between two writes. Native
 * sending is 10 s with a delay (`livePublish.ts` → `nativeLiveIntervalMs`),
 * plus waiting for the next sample and the network. If shorter, the playhead
 * catches up with the data and the picture stalls until the next write.
 */
export const LIVE_PLAYBACK_LAG_MS = 12_000;

/** Time span of the tail — fits the buffer plus one full write gap. */
export const LIVE_TAIL_SPAN_MS = 30_000;

/** Keeps the document small with dense sampling: at most this many tail points. */
export const LIVE_TAIL_MAX_POINTS = 60;

export interface LiveTailEntry {
  /** Point time relative to the cut (≤ 0, ms). */
  dtMs: number;
  /** The recorder's cumulative distance at this point. */
  distanceM: number;
}

/**
 * Content of the `tail` field. The entries belong to the LAST
 * `entries.length` points of `route`, in order — so coordinates are not repeated.
 */
export interface LiveTail {
  /** How far the viewer's playhead stays behind the cut. */
  lagMs: number;
  /** Distance at the last point before the tail (end of the head). */
  baseDistanceM: number;
  entries: LiveTailEntry[];
}

/**
 * Public route plus tail. The head is simplified, the tail is appended to the
 * polyline WITHOUT simplification — so the viewer knows exactly which route
 * point belongs to which timestamp. Old viewers and `/live/battle` still read
 * `route` as the full route.
 *
 * @param distances the recorder's cumulative distance per point, same length as `points`
 */
export function publicRouteWithTail(
  points: readonly TracePoint[],
  distances: readonly number[],
  cutoff: number,
  lagMs: number,
): { route: string; tail: string } {
  if (points.length < 2) return { route: '', tail: '' };
  let split = points.length;
  while (split > 0 && points.length - split < LIVE_TAIL_MAX_POINTS && points[split - 1]!.t > cutoff - LIVE_TAIL_SPAN_MS) {
    split -= 1;
  }
  const head = points.slice(0, split);
  const tailPoints = points.slice(split);
  const reducedHead = head.length > 0 ? reduceRoute(head, MAX_PUBLISH_POINTS - tailPoints.length) : [];
  const tail: LiveTail = {
    lagMs,
    baseDistanceM: split > 0 ? distances[split - 1] ?? 0 : 0,
    entries: tailPoints.map((point, index) => ({
      dtMs: point.t - cutoff,
      distanceM: distances[split + index] ?? 0,
    })),
  };
  return { route: encodePolyline([...reducedHead, ...tailPoints]), tail: encodeLiveTail(tail) };
}

/** Compact text: `lag;base;dt:dist,dt:dist,…` — a ~30-point tail is ~0.5 KB. */
export function encodeLiveTail(tail: LiveTail): string {
  const entries = tail.entries.map((entry) => `${Math.round(entry.dtMs)}:${round1(entry.distanceM)}`).join(',');
  return `${Math.round(tail.lagMs)};${round1(tail.baseDistanceM)};${entries}`;
}

/** Defensive read: any unexpected shape → `null`, and the viewer works the old way. */
export function decodeLiveTail(value: unknown): LiveTail | null {
  if (typeof value !== 'string' || value === '') return null;
  const [lag, base, list, extra] = value.split(';');
  if (lag === undefined || base === undefined || list === undefined || extra !== undefined) return null;
  const lagMs = Number(lag), baseDistanceM = Number(base);
  if (!Number.isFinite(lagMs) || lagMs < 0 || !Number.isFinite(baseDistanceM) || baseDistanceM < 0) return null;
  const entries: LiveTailEntry[] = [];
  for (const item of list === '' ? [] : list.split(',')) {
    const [dt, distance] = item.split(':');
    const dtMs = Number(dt), distanceM = Number(distance);
    if (!Number.isFinite(dtMs) || dtMs > 0 || !Number.isFinite(distanceM) || distanceM < 0) return null;
    if (entries.length > 0 && dtMs < entries[entries.length - 1]!.dtMs) return null;
    entries.push({ dtMs, distanceM });
  }
  return { lagMs, baseDistanceM, entries };
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
